import { TFunction } from 'i18next';
import {
    ActionType,
    ActivityLogObject,
    ActivityLogsModel,
} from '../../types/generated/activityLogsModel';

export const getActionTypeLabel = (
    action: ActionType,
    t: TFunction
): string => {
    switch (action) {
        case ActionType.Undefined:
            return t('form-content.logs:activityLog.actionUndefined');
        case ActionType.FormChanged:
            return t('form-content.logs:activityLog.actionFormChanged');
        case ActionType.FormDeleted:
            return t('form-content.logs:activityLog.actionFormDeleted');
        case ActionType.UserUpdated:
            return t('form-content.logs:activityLog.actionUserUpdated');
        case ActionType.UserDeleted:
            return t('form-content.logs:activityLog.actionUserDeleted');
        case ActionType.HttpRepFormChanged:
            return t(
                'form-content.logs:activityLog.actionHttpRepFormChanged'
            );
        default:
            return String(action);
    }
};

export type ActivityOperation =
    | 'Create'
    | 'Update'
    | 'UpdateLockedFields'
    | 'Publish'
    | 'Translate'
    | 'Lock'
    | 'Other';

const pathSegmentToOperation: Record<string, ActivityOperation> = {
    create: 'Create',
    update: 'Update',
    updatelockedfields: 'UpdateLockedFields',
    publish: 'Publish',
    translate: 'Translate',
    lock: 'Lock',
};

export const getOperationLabel = (
    operation: ActivityOperation,
    t: TFunction
): string => {
    switch (operation) {
        case 'Create':
            return t('form-content.logs:activityLog.operationCreate');
        case 'Update':
            return t('form-content.logs:activityLog.operationUpdate');
        case 'UpdateLockedFields':
            return t(
                'form-content.logs:activityLog.operationUpdateLockedFields'
            );
        case 'Publish':
            return t('form-content.logs:activityLog.operationPublish');
        case 'Translate':
            return t('form-content.logs:activityLog.operationTranslate');
        case 'Lock':
            return t('form-content.logs:activityLog.operationLock');
        case 'Other':
            return t('form-content.logs:activityLog.operationOther');
        default:
            return t('form-content.logs:activityLog.operationOther');
    }
};

export const parseOperationFromRequestPath = (
    requestPath: string | undefined
): ActivityOperation => {
    if (!requestPath) {
        return 'Other';
    }

    const normalized = requestPath.toLowerCase();
    const repFormIndex = normalized.indexOf('/rep/form/');
    const pathAfterRepForm =
        repFormIndex >= 0
            ? normalized.substring(repFormIndex + '/rep/form/'.length)
            : normalized;

    const segment = pathAfterRepForm.split(/[/?\s]/)[0];
    return pathSegmentToOperation[segment] ?? 'Other';
};

export const formatRequestBodyForDisplay = (
    data: string[] | undefined
): string => {
    const requestBody = data?.[1];
    if (!requestBody) {
        return '';
    }

    try {
        return JSON.stringify(JSON.parse(requestBody), null, 2);
    } catch {
        return requestBody;
    }
};

export const formatRequestBodyForCopy = (
    data: string[] | undefined
): string => formatRequestBodyForDisplay(data);

export const isFormActivityEntry = (entry: ActivityLogsModel): boolean =>
    entry.affectedObject === ActivityLogObject.Form;

export const canDiffEntry = (entry: ActivityLogsModel): boolean =>
    isFormActivityEntry(entry) && Boolean(entry.data?.[1]);
